import {
  createAnimation,
  createGesture,
  IonCard,
  IonCardContent,
  IonCol,
  IonGrid,
  IonImg,
  IonRow,
  IonText,
} from '@ionic/react';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import { Button } from '@/components/Button';
// Styles
import styles from '@/components/Cards/ClientCard.module.css';
// Components
import { Icon } from '@/components/Icon';
import { IconRotate } from '@/components/Icon/types';

import { convertDate } from '@/utils/convertFirebaseDate';
import { mergeClassNames } from '@/utils/mergeClassNames';

import userService from '@/hooks/useClients/userService';

// Hooks & Models
import { Client, ExportedClient } from '@/models';

import type { Animation, Gesture, GestureDetail } from '@ionic/react';

// Images

type ClientCardProps = {
  client: ExportedClient;
  location?: string;
};

let activeCardId: string | null = null;

/**
 * Component that renders a card displaying client information.
 *
 * @param {ClientCardProps} props - The properties for the ClientCard component.
 * @param {Object} props.client - The client object containing information to display.
 * @param {string} props.client.name - The name of the client.
 * @param {string} props.client.dateOfBirth - The date of birth of the client.
 * @param {string} props.client.lastRecording - The date of the client's last recording.
 *
 * @returns {JSX.Element} A JSX element representing the client card.
 */
// TODO: Add Archived state
export const ClientCard = ({ client, location }: ClientCardProps) => {
  const dateOfBirth = convertDate(client.dateOfBirth);
  const lastSession = client.lastSession ? convertDate(client.lastSession) : '-';
  const history = useHistory();
  const containerEl = useRef<HTMLDivElement | null>(null);
  const MAX_TRANSLATE = -100; // Move left 100px
  const [isVisible, setIsVisible] = useState(false);
  const path = location ? `/${location}/${client.id}` : `/clients/${client.id}`;

  const handleClick = () => {
    if (containerEl.current?.style.transform === '' || containerEl.current?.style.transform === 'translateX(0px)') {
      history.push(path, {
        clientName: `${client.firstName} ${client.lastName}`,
        clientId: client.id,
        resetActiveSession: true,
        measurementType: location,
      });
    }
  };

  const handleArchive = (event: any) => {
    event.stopPropagation();
    console.log('Archive client:', client.id);
    setIsVisible(false);
    userService.archiveClient(client.id);
    // Reset content position after archiving
    if (containerEl.current) {
      const animation: Animation = createAnimation()
        .addElement(containerEl.current)
        .duration(200)
        .fromTo('transform', `translateX(${MAX_TRANSLATE}px)`, 'translateX(0px)');
      animation.play();
      activeCardId = null;
    }
  };

  useEffect(() => {
    if (containerEl.current) {
      const gesture = createGesture({
        el: containerEl.current,
        threshold: 0,
        gestureName: `card-content-drag-${client.id}`,
        onStart: () => {
          // Reset previously swiped card
          if (activeCardId && activeCardId !== client.id) {
            const prevContent = document.getElementById(`content-${activeCardId}`);
            const prevButton = document.getElementById(`button-${activeCardId}`);
            if (prevContent) {
              prevContent.style.transform = 'translateX(0px)';
            }
            if (prevButton) {
              prevButton.style.visibility = 'hidden';
            }
          }
          activeCardId = client.id;
        },
        onMove: (event) => {
          const deltaX = Math.min(0, event.deltaX);
          containerEl.current!.style.transform = `translateX(${deltaX}px)`;
        },
        onEnd: (event) => {
          const threshold = MAX_TRANSLATE / 2;
          if (event.deltaX <= threshold) {
            containerEl.current!.style.transform = `translateX(${MAX_TRANSLATE}px)`;
            setIsVisible(true);
          } else {
            containerEl.current!.style.transform = `translateX(0px)`;
            setIsVisible(false);
            if (activeCardId === client.id) {
              activeCardId = null;
            }
          }
        },
      });
      gesture.enable(true);
    }
  }, [containerEl, client.id]);

  return (
    <IonRow className='ion-no-padding ion-align-items-center' style={{ marginBottom: '24px' }}>
      <div className={styles.cardContainer}>
        <IonCard className={styles.card} id={`card-${client.id}`}>
          <div className={styles.swipeableContainer} id={`content-${client.id}`} ref={containerEl}>
            <IonCardContent className={styles.cardContent} onClick={handleClick}>
              <IonRow className='ion-align-items-center'>
                <IonCol className='ion-no-padding' size='1'>
                  <Icon className={styles.icon} color='#2DD2BE' name='user' />
                </IonCol>
                <IonCol className='ion-no-padding' size='6'>
                  <IonText className={styles.name}>{`${client.firstName} ${client.lastName}`}</IonText>
                </IonCol>
                <IonCol className='ion-no-padding ion-text-center' size='2'>
                  <IonText className={styles.dob}>{dateOfBirth}</IonText>
                </IonCol>
                <IonCol className='ion-no-padding ion-text-center' size='2'>
                  <IonText className={styles.lastRecording}>{lastSession}</IonText>
                </IonCol>
                <IonCol className='ion-no-padding' size='auto' style={{ marginLeft: 'auto' }}>
                  <Icon
                    className={mergeClassNames(styles.icon, styles.chevron)}
                    color='#829696'
                    direction={IconRotate.East}
                    name='chevron'
                  />
                </IonCol>
              </IonRow>
            </IonCardContent>
          </div>

          {/* Archive Button */}
          <div
            className={styles.archiveButton}
            id={`button-${client.id}`}
            style={{ visibility: isVisible ? 'visible' : 'hidden' }}>
            <Button leadingIcon='archive' onClick={handleArchive} size='medium' variant='danger' />
          </div>
        </IonCard>
      </div>
    </IonRow>
  );
};
